import { readLocalStorageValue, useLocalStorage } from '@mantine/hooks';
import {
  EmployeesReportColumn,
  EmployeesReportDistinctValuesDto,
  EmployeesReportFilter,
  EmployeesReportRowDto,
  EmployeesReportView,
  EmployeesReportViewCountsDto,
  Meta,
  SortDirection,
} from '@zorro/clients';
import { useDebouncedValue, useMonolithQuery } from '@zorro/web/ui-utils';
import { isEqual } from 'lodash';
import {
  parseAsInteger,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import { useEffect, useMemo } from 'react';

import { mapDatatableFilters } from '../DatatableUtils';
import { Filter } from '../Filters';
import { EmployeesDatatableStoredParams } from './employeesDatatable.types';

export const defaultQueryParams: EmployeesDatatableStoredParams = {
  view: EmployeesReportView.ALL,
  filters: [],
  searchValue: '',
  page: 1,
  sortStatus: {
    columnAccessor: EmployeesReportColumn.employeeFullName,
    direction: SortDirection.asc,
  },
  pageSize: 50,
};

const employeeDatatableQueryParams = {
  view: parseAsStringEnum<EmployeesReportView>(
    Object.values(EmployeesReportView)
  ).withDefault(EmployeesReportView.ALL),
  filters: parseAsJson<Filter<EmployeesReportRowDto>[]>((value) =>
    Array.isArray(value) ? (value as Filter<EmployeesReportRowDto>[]) : null
  ).withDefault([]),
  searchValue: parseAsString.withDefault(''),
  page: parseAsInteger.withDefault(1),
  sortStatus: parseAsJson<{ columnAccessor: string; direction: string }>(
    (value) =>
      typeof value === 'object' && value !== null
        ? (value as { columnAccessor: string; direction: string })
        : null
  ).withDefault({
    columnAccessor: EmployeesReportColumn.employeeFullName,
    direction: SortDirection.asc,
  }),
  pageSize: parseAsInteger.withDefault(50),
};

export function usePaginatedEmployeeReports(
  employerId: string,
  selectedEnrollmentPeriodId: string
): {
  isLoading: boolean;
  pageRecords: EmployeesReportRowDto[];
  counts?: EmployeesReportViewCountsDto;
  filterOptions?: EmployeesReportDistinctValuesDto[];
  meta?: Meta;
  employeesDatatableParams: EmployeesDatatableStoredParams;
  setEmployeesDatatableParams: (
    value:
      | EmployeesDatatableStoredParams
      | ((
          prevState: EmployeesDatatableStoredParams
        ) => EmployeesDatatableStoredParams)
  ) => void;
} {
  const [queryParams, setQueryParams] = useQueryStates(
    employeeDatatableQueryParams
  );
  type QueryParamsType = typeof queryParams;

  const localStorageKey = `employeesDatatableParams_${employerId}`;
  const defaultValueForEmployeesDatatableFilters = useMemo(
    () =>
      readLocalStorageValue<EmployeesDatatableStoredParams>({
        key: localStorageKey,
      }) || defaultQueryParams,
    [localStorageKey]
  );
  const [employeesDatatableParams, setEmployeesDatatableParams] =
    useLocalStorage<EmployeesDatatableStoredParams>({
      key: localStorageKey,
      defaultValue: defaultValueForEmployeesDatatableFilters,
    });

  const {
    view,
    sortStatus,
    filters,
    searchValue,
    page,
    pageSize = defaultQueryParams.pageSize,
  } = employeesDatatableParams;

  const hasCustomQueryParams = (queryParams: QueryParamsType): boolean => {
    return !isEqual(queryParams, defaultQueryParams);
  };

  useEffect(() => {
    if (hasCustomQueryParams(queryParams)) {
      setEmployeesDatatableParams({
        view: queryParams.view,
        filters: queryParams.filters,
        searchValue: queryParams.searchValue,
        page: queryParams.page,
        pageSize: queryParams.pageSize,
        sortStatus: {
          columnAccessor:
            queryParams.sortStatus.columnAccessor ||
            defaultQueryParams.sortStatus.columnAccessor,
          direction:
            queryParams.sortStatus.direction === SortDirection.asc ||
            queryParams.sortStatus.direction === SortDirection.desc
              ? queryParams.sortStatus.direction
              : SortDirection.asc,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (!isEqual(employeesDatatableParams, queryParams)) {
      void setQueryParams(employeesDatatableParams);
    }
  }, [employeesDatatableParams, queryParams, setQueryParams]);

  const { debounced: debouncedFilters } = useDebouncedValue({
    value: filters,
    wait: 300,
  });

  const { debounced: debouncedSortStatus } = useDebouncedValue({
    value: sortStatus,
    wait: 150,
  });

  const filterBy = useMemo(
    () =>
      mapDatatableFilters<
        EmployeesReportColumn,
        EmployeesReportRowDto,
        EmployeesReportFilter
      >(debouncedFilters),
    [debouncedFilters]
  );

  const { debounced: debouncedSearchValue } = useDebouncedValue({
    value: searchValue,
    wait: 300,
  });

  const { data: paginatedResponse, isFetching } = useMonolithQuery({
    method: 'employeeReportsControllerGenerateEmployeesReport',
    params: [
      employerId,
      selectedEnrollmentPeriodId,
      {
        view,
        sortBy: debouncedSortStatus.columnAccessor as EmployeesReportColumn,
        sortDirection: debouncedSortStatus.direction as SortDirection,
        filterBy,
        searchTerm: debouncedSearchValue,
        perPage: pageSize,
        page,
      },
    ],
    enabled: Boolean(employerId && selectedEnrollmentPeriodId),
  });

  return {
    isLoading: isFetching || !selectedEnrollmentPeriodId,
    pageRecords: paginatedResponse?.data ?? [],
    counts: paginatedResponse?.counts,
    filterOptions: paginatedResponse?.filterOptions,
    meta: paginatedResponse?.meta,
    employeesDatatableParams,
    setEmployeesDatatableParams,
  };
}
